import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import useInitialData from 'src/hooks/useInitialData';
import { HiOutlineArrowNarrowRight } from 'react-icons/hi';
import Link from 'next/link';
import { Modal } from 'react-bootstrap';
import { useRouter } from 'next/router';

const SITE_BANNER_HEIGHT = 24
const HIDDEN_ROUTES = ['/'];

export function calcBannerStickyTop(scrollY?: number) {
  const headerEle = document.querySelector('#js-header');
  const headerHeight = Number(headerEle?.clientHeight || 0);
  const scrollPos = scrollY || window.scrollY
  return Math.max(headerHeight, headerHeight + SITE_BANNER_HEIGHT - scrollPos);
}

const SiteBanners: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentBanner, setCurrentBanner] = useState<null | any>(null);
  const initialData = useInitialData();
  const router = useRouter();
  const siteBanners = HIDDEN_ROUTES.includes(router.pathname) ? [] : initialData.siteBanners
  useEffect(() => {
    setCurrentIndex(0);
    // no rotation for 0 banners (hidden routes) — `% 0` would set the index to NaN
    if (siteBanners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % siteBanners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [siteBanners.length]);

  useEffect(() => {
    const body = window.document.querySelector('body')
    if (body && siteBanners.length > 0) {
      body.classList.add('has-banner')
      const toggleClass = () => {
        const shouldRemove = window.scrollY > SITE_BANNER_HEIGHT
        if (shouldRemove) {
          body.classList.remove('has-banner')
        } else {
          body.classList.add('has-banner')
        }
      }

      window.document.addEventListener('scroll', toggleClass)
      return () => {
        window.document.removeEventListener('scroll', toggleClass)
        // the banner is hidden on some routes, so don't leave its class on the body
        body.classList.remove('has-banner')
      }
    }
  }, [siteBanners.length])

  return siteBanners.length > 0 ? (
    <>
      <div
        className={classNames(
          'tw-relative tw-w-full tw-overflow-hidden tw-bg-brand-green tw-transition-all tw-duration-200 tw-ease-in-out tw-h-6',
        )}
      >
        {siteBanners.map((siteBanner, index) => (
          <div
            key={index}
            className={classNames(
              'tw-absolute tw-w-full tw-left-0 tw-top-1/2 tw-transform tw-transition-transform tw-duration-700 tw-ease-in-out tw-leading-none tw-pb-1 tw-text-center',
              {
                'tw-invisible tw-translate-y-full': currentIndex !== index,
                'tw--translate-y-1/2': currentIndex === index
              }
            )}
          >
            {siteBanner.kind === 'modal' ? (
              <span
                className={classNames('tw-text-brand-navy tw-text-xs tw-leading-none', {
                  'tw-cursor-pointer': siteBanner.content,
                })}
                onClick={() => {
                  if (siteBanner.kind === 'modal' && siteBanner.content) {
                    setCurrentBanner(siteBanner);
                  }
                }}
              >
                {siteBanner.title}
              </span>
            ) : siteBanner.content ? (
              <Link legacyBehavior prefetch={false} href={siteBanner.content}>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-brand-navy tw-cursor-pointer hover:tw-underline tw-text-xs tw-leading-none"
                >
                  <span className="tw-mr-2">{siteBanner.title}</span>
                  <HiOutlineArrowNarrowRight strokeWidth={2} />
                </a>
              </Link>
            ) : (
              <span
                className="tw-text-brand-navy tw-text-xs tw-leading-none"
              >
                {siteBanner.title}
              </span>
            )}
          </div>
        ))}
      </div>

      {currentBanner ? (
        <Modal
          show={currentBanner ? true : false}
          onHide={() => setCurrentBanner(null)}
          dialogClassName="modal-dialog-centered"
        >
          <Modal.Body>
            <div className="tw-text-left md:tw-text-center tw-font-semibold tw-mb-2">
              {currentBanner.title}
            </div>
            <div className="tw-text-left md:tw-text-center">{currentBanner.content}</div>
          </Modal.Body>
        </Modal>
      ) : null}
    </>
  ) : null;
};

export default SiteBanners;
